import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ACTIONS_COL_WIDTH,
  Alert,
  Badge,
  Button,
  DataTable,
  OverlayTrigger,
  PageHeader,
  Tab,
  Tabs,
  Tooltip,
} from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { ExpiryIndicator } from '../../../components/ExpiryIndicator';
import {
  RESOURCE_TYPE_LABELS,
  ResourceRate,
  Vendor,
  VendorResource,
  groupResourcesByType,
  vendorService,
} from '../../../services/vendorService';
import { currency, shortDate } from '../../vendors/utils/format';

/**
 * HE-M17 — the ops coordinator's own administration screen ("Vendor Admin").
 *
 * Deliberately separate from system configuration, which stays with super users. Ops
 * coordinators have never had admin access, so this is a new permission set rather than a
 * widening of an existing admin role.
 *
 * The Approvals tab is where the permission split becomes visible: opening the screen
 * requires heMaster:vendor_approve. A coordinator holding that key (and resource_manage)
 * sees the queue but gets no approve buttons. Vendor activation is allowed for anyone
 * with heMaster:vendor_activate, including the person who created the vendor. Rate
 * changes still disable self-approval, with the reason on hover.
 */
const HeAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();

  const [pendingVendors, setPendingVendors] = useState<Vendor[]>([]);
  const [pendingRates, setPendingRates] = useState<ResourceRate[]>([]);
  const [resources, setResources] = useState<VendorResource[]>([]);
  const [resourceSortBy, setResourceSortBy] = useState<string | null>(null);
  const [resourceSortDir, setResourceSortDir] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canActivate = hasPermission('heMaster:vendor_activate');
  const canApproveRates = hasPermission('heMaster:rate_approve');
  const currentUserId = currentUser?.id;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [vendors, rates, resourcePage] = await Promise.all([
        vendorService.listPendingActivation(),
        vendorService.listPendingRates(),
        vendorService.getResourcesPaginated({ page: 1, limit: 100 }),
      ]);
      setPendingVendors(vendors);
      setPendingRates(rates);
      setResources(resourcePage.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load administration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (fn: () => Promise<unknown>) => {
    try {
      setActionError(null);
      await fn();
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    }
  };

  /** Disabled with a reason beats hidden: the coordinator learns why, not just that nothing happens. */
  const ApprovalButton: React.FC<{
    isOwn: boolean;
    permitted: boolean;
    label: string;
    variant: 'primary' | 'outline-danger';
    onClick: () => void;
  }> = ({ isOwn, permitted, label, variant, onClick }) => {
    if (!permitted) return null;
    if (isOwn) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`own-${label}`}>
              You submitted this, so someone else has to review it.
            </Tooltip>
          }
        >
          <span className="tw-inline-block">
            <Button variant={variant} size="sm" disabled>
              {label}
            </Button>
          </span>
        </OverlayTrigger>
      );
    }
    return (
      <Button variant={variant} size="sm" onClick={onClick}>
        {label}
      </Button>
    );
  };

  const vendorColumns: DataTableColumn<Vendor>[] = [
    { key: 'name', header: 'Vendor', mobile: 'title', cell: (v) => v.name },
    {
      key: 'serviceAreas',
      header: 'Service Areas',
      mobile: 'subtitle',
      cell: (v) => v.serviceAreas?.map((sa) => sa.serviceArea.name).join(', ') || '—',
    },
    {
      key: 'createdBy',
      header: 'Entered By',
      cell: (v) => (v.createdBy ? `${v.createdBy.name} ${v.createdBy.lastName}` : '—'),
    },
    {
      key: 'compliance',
      header: 'Compliance',
      cell: (v) =>
        v.compliance?.hasIssue ? (
          <ExpiryIndicator compliance={v.compliance} showLabel />
        ) : (
          <span className="tw-text-success">
            <i className="bi bi-check-circle tw-mr-1" />
            Current
          </span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      mobile: 'trailing',
      cell: (v) => (
        <div className="tw-flex tw-gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(`/vendors/${v.id}`)}>
            Open
          </Button>
          {canActivate && !v.compliance?.hasIssue && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => runAction(() => vendorService.activateVendor(v.id))}
            >
              Activate
            </Button>
          )}
        </div>
      ),
    },
  ];

  const rateColumns: DataTableColumn<ResourceRate>[] = [
    {
      key: 'resource',
      header: 'Resource',
      mobile: 'title',
      cell: (r) => r.resource?.name ?? r.resourceId,
    },
    {
      key: 'vendor',
      header: 'Vendor',
      mobile: 'subtitle',
      cell: (r) => r.resource?.vendor?.name ?? '—',
    },
    { key: 'serviceArea', header: 'Service Area', cell: (r) => r.serviceArea?.name ?? '—' },
    {
      key: 'change',
      header: 'Change',
      cell: (r) => (
        <span>
          {r.supersedes ? `${currency(r.supersedes.rate)} → ` : 'New: '}
          <strong>{currency(r.rate)}</strong>
        </span>
      ),
    },
    { key: 'effectiveFrom', header: 'Effective From', cell: (r) => shortDate(r.effectiveFrom) },
    {
      key: 'effectiveTo',
      header: 'Effective To',
      cell: (r) => (r.effectiveTo ? shortDate(r.effectiveTo) : '—'),
    },
    {
      key: 'requestedBy',
      header: 'Requested By',
      cell: (r) => (r.requestedBy ? `${r.requestedBy.name} ${r.requestedBy.lastName}` : '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      mobile: 'trailing',
      cell: (r) => {
        const isOwn = !!currentUserId && r.requestedById === currentUserId;
        return (
          <div className="tw-flex tw-gap-2">
            <ApprovalButton
              isOwn={isOwn}
              permitted={canApproveRates}
              label="Approve"
              variant="primary"
              onClick={() => runAction(() => vendorService.approveRate(r.id))}
            />
            <ApprovalButton
              isOwn={isOwn}
              permitted={canApproveRates}
              label="Reject"
              variant="outline-danger"
              onClick={() => runAction(() => vendorService.rejectRate(r.id))}
            />
          </div>
        );
      },
    },
  ];

  const resourceColumns: DataTableColumn<VendorResource>[] = [
    { key: 'name', header: 'Resource', mobile: 'title', cell: (r) => r.name },
    {
      key: 'vendor',
      header: 'Vendor',
      sortable: true,
      mobile: 'subtitle',
      cell: (r) => r.vendor?.name ?? '—',
    },
    {
      key: 'resourceType',
      header: 'Type',
      cell: (r) => (
        <span>
          {RESOURCE_TYPE_LABELS[r.resourceType]}
          {r.isFixedPrice && (
            <Badge bg="secondary" className="tw-ml-2">
              Fixed
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'effortType',
      header: 'Markup bucket',
      cell: (r) =>
        r.effortType ? r.effortType : <span className="tw-text-content-muted">No markup</span>,
    },
    {
      key: 'serviceAreas',
      header: 'Areas',
      cell: (r) => r.serviceAreas?.map((sa) => sa.serviceArea.name).join(', ') || '—',
    },
    {
      key: 'isActive',
      header: 'Status',
      mobile: 'trailing',
      cell: (r) =>
        r.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>,
    },
  ];

  const approvalCount = pendingVendors.length + pendingRates.length;

  const resourceGroups = useMemo(() => {
    let list = resources;
    if (resourceSortBy === 'vendor') {
      list = [...resources].sort((a, b) => {
        const cmp = (a.vendor?.name ?? '').localeCompare(b.vendor?.name ?? '', undefined, {
          sensitivity: 'base',
        });
        return resourceSortDir === 'asc' ? cmp : -cmp;
      });
    }
    return groupResourcesByType(list);
  }, [resources, resourceSortBy, resourceSortDir]);

  return (
    <div className="he-admin-page tw-p-4">
      <PageHeader
        title="Vendor Admin"
        actions={[
          {
            key: 'vendors',
            label: 'All Vendors',
            icon: 'bi-building',
            intent: 'secondary',
            onClick: () => navigate('/vendors'),
          },
          {
            key: 'refresh',
            label: 'Refresh',
            icon: 'bi-arrow-clockwise',
            // Refresh is chrome, not an outcome - neutral, like WorkIdsPage and ProjectsView.
            intent: 'neutral',
            onClick: load,
          },
        ]}
      />

      {error && <Alert variant="danger" className="tw-mb-3">{error}</Alert>}
      {actionError && <Alert variant="danger" className="tw-mb-3">{actionError}</Alert>}

      {!canActivate && !canApproveRates && approvalCount > 0 && (
        <Alert variant="info" className="tw-mb-3">
          There {approvalCount === 1 ? 'is' : 'are'} {approvalCount} item
          {approvalCount === 1 ? '' : 's'} awaiting approval. You can see the queue but not approve
          from it — approval is a separate permission held by a second person.
        </Alert>
      )}

      <Tabs defaultActiveKey="approvals" id="he-admin-tabs">
        <Tab eventKey="approvals" title={`Pending Approvals (${approvalCount})`}>
          <div>
            <h6 className="tw-mb-2">
              Vendors awaiting activation ({pendingVendors.length})
            </h6>
            <DataTable
              columns={vendorColumns}
              data={pendingVendors}
              rowKey={(v) => v.id}
              loading={loading}
              framed
              emptyMessage="No vendors are waiting for activation."
              className="tw-mb-4"
            />

            <h6 className="tw-mb-2">
              Rate changes awaiting approval ({pendingRates.length})
            </h6>
            <DataTable
              columns={rateColumns}
              data={pendingRates}
              rowKey={(r) => r.id}
              loading={loading}
              framed
              emptyMessage="No rate changes are waiting for approval."
            />
          </div>
        </Tab>

        <Tab eventKey="resources" title={`Resources (${resources.length})`}>
          <div>
            {/* Same grouping as the vendor detail table — see groupResourcesByType. */}
            {resources.length === 0 ? (
              <DataTable
                columns={resourceColumns}
                data={[]}
                rowKey={(r) => r.id}
                loading={loading}
                framed
                emptyMessage="No resources have been set up yet."
              />
            ) : (
              resourceGroups.map(({ group, resources: groupResources }) => (
                <div key={group} className="tw-mb-4">
                  <h6 className="tw-mb-2 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-secondary">
                    {group}{' '}
                    <span className="tw-font-normal tw-text-content-muted">
                      ({groupResources.length})
                    </span>
                  </h6>
                  <DataTable
                    columns={resourceColumns}
                    data={groupResources}
                    rowKey={(r) => r.id}
                    framed
                    sortBy={resourceSortBy}
                    sortDir={resourceSortDir}
                    onSort={(key, dir) => {
                      setResourceSortBy(key);
                      setResourceSortDir(dir);
                    }}
                    onRowClick={(r) => navigate(`/vendors/${r.vendorId}`)}
                    emptyMessage=""
                  />
                </div>
              ))
            )}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default HeAdminPage;
